/**
 * Popup — generic popup wrapper. Listens (delegated on document.body) for
 * `[data-action="toggle-popup"]` clicks, ESC keydown, `drawer:close-other`
 * and `edit-img:open-popup` events. Maintains a stack of nested popups,
 * handles overlay clicks, body scroll lock, focus trap, transition cleanup.
 *
 * Loaded as ES module via `@theme/popup` importmap entry.
 * Exports both the class and a pre-built `globalPopup` singleton.
 * Module is loaded globally from `layout/theme.liquid` so the singleton
 * registers its document-level listeners on every page.
 *
 * Dependencies:
 *   - @theme/delegate
 *   - @theme/body-scroll-lock-options
 *   - @theme/dynamic-variables
 *   - @theme/body-scroll-lock
 *   - @theme/focus-trap
 */

import { Delegate } from '@theme/delegate';
import { bodyScrollLockOptions } from '@theme/body-scroll-lock-options';
import { dynamicVars } from '@theme/dynamic-variables';
import { trapFocus, removeTrapFocus } from '@theme/focus-trap';
import { disableBodyScroll, enableBodyScroll } from '@theme/body-scroll-lock';

export class Popup {
  constructor() {
    this.delegateBody = new Delegate(document.body);
    this.activePopups = [];
    this.onPopupOpen = null;
    this.onPopupClose = null;
    this.initPopupElements();
    this.bindEvents();
  }

  initPopupElements() {
    document.querySelectorAll('[data-overlay]').forEach((popupElement) => {
      if (popupElement.style.display !== 'none') {
        popupElement.style.display = 'none';
      }
      if (!popupElement.hasAttribute('aria-hidden')) {
        popupElement.setAttribute('aria-hidden', 'true');
      }
      if (!popupElement.hasAttribute('aria-expanded')) {
        popupElement.setAttribute('aria-expanded', 'false');
      }
      if (!popupElement.hasAttribute('inert')) {
        popupElement.setAttribute('inert', '');
      }
    });
  }

  bindEvents() {
    this.delegateBody.on(
      'click',
      '[data-action="toggle-popup"]',
      this.handleOpenOptions.bind(this)
    );
    this.delegateBody.on(
      'drawer:close-other',
      this.handleDrawerCloseOther.bind(this)
    );
    this.delegateBody.on(
      'edit-img:open-popup',
      this.handleOpenOptions.bind(this)
    );
    this.delegateBody.on('keydown', this.handleEscape.bind(this));

    // Close any open popup (e.g. the localization selectors) when another
    // top-bar surface takes over: the desktop mega-menu (menu:open), a drawer
    // such as cart / mobile menu (drawer:opening), or the search overlay
    // (search:opening). All three bubble to / fire on document.
    this._closeForOtherSurface = () => {
      if (this.activePopups.length) this.closeAllPopups();
    };
    ['menu:open', 'drawer:opening', 'search:opening'].forEach((evt) =>
      document.addEventListener(evt, this._closeForOtherSurface)
    );
  }

  handleOpenOptions(event) {
    event.stopPropagation();
    event.preventDefault();

    dynamicVars.syncHeight();
    const toggleButton = event.target.closest('[data-action="toggle-popup"]');
    if (toggleButton) {
      this.togglePopup(toggleButton);
      return;
    }

    if (this.activePopups.length) {
      const topPopup = this.activePopups[this.activePopups.length - 1];
      const overlay = this.getOverlayElement(topPopup);
      if (overlay && overlay.contains(event.target)) {
        this.closePopup(topPopup);
      }
    }
  }

  handleEscape(event) {
    if (event.key !== 'Escape' || !this.activePopups.length) return;

    event.preventDefault();
    event.stopPropagation();
    const topPopup = this.activePopups[this.activePopups.length - 1];
    const toggleBtn = topPopup.querySelector('[data-action="toggle-popup"]');
    if (toggleBtn) {
      toggleBtn.click();
    } else {
      this.closePopup(topPopup);
    }
  }

  togglePopup(button) {
    const popupId = button.getAttribute('aria-controls');
    if (!popupId) return;

    const popupElement = document.getElementById(popupId);
    if (!popupElement) return;

    const isChild = button.getAttribute('data-child-popup') === 'true';

    if (popupElement.getAttribute('aria-hidden') === 'false') {
      this.closePopup(popupElement);
    } else {
      this.openPopup(popupElement, isChild);
    }
  }

  openPopup(popupElement, isChild = false) {
    const beforeOpenEvent = new CustomEvent('popup:before-open', {
      bubbles: true,
      cancelable: true,
      detail: { isChild },
    });
    popupElement.dispatchEvent(beforeOpenEvent);
    if (beforeOpenEvent.defaultPrevented) return;

    // A popup and the other top-level surfaces (drawers, mega-menu, search
    // overlay) are mutually exclusive: opening a popup collapses any open
    // dropdown (`menu:close`) and `popup:opening` closes drawers and search.
    // The reverse (`drawer:opening` / `menu:open` / `search:opening` ->
    // `closeAllPopups`) is wired in bindEvents. A child popup stacks on its
    // parent surface, so it must not close either.
    if (!isChild) {
      document.body.dispatchEvent(new CustomEvent('menu:close', { bubbles: true }));
      document.body.dispatchEvent(new CustomEvent('popup:opening', { bubbles: true }));
    }

    popupElement._isChild = isChild;
    popupElement.style.display = 'none';
    // Force a reflow so display:none -> display:block transition is reliable.
    void popupElement.offsetHeight;
    popupElement.style.display = '';

    if (!isChild) {
      while (this.activePopups.length) {
        this._closePopup(this.activePopups.pop());
      }
    }

    popupElement.setAttribute('aria-hidden', 'false');
    popupElement.removeAttribute('inert');
    requestAnimationFrame(() => {
      popupElement.setAttribute('aria-expanded', 'true');
      popupElement.dispatchEvent(
        new CustomEvent('popup:opened', {
          bubbles: true,
          detail: { isChild },
        })
      );
      if (typeof this.onPopupOpen === 'function') {
        this.onPopupOpen(popupElement);
      }
    });

    const focus = popupElement.querySelector('[data-scrollable]');
    if (focus) {
      popupElement._scrollableElement = focus;
      disableBodyScroll(focus, bodyScrollLockOptions);
    }

    trapFocus(popupElement);

    const overlay = this.getOverlayElement(popupElement);
    if (overlay) {
      overlay.classList.add('is-visible');
      overlay.classList.add('is-popup-overlay');
      const handleOverlayClick = (e) => {
        e.preventDefault();
        this.closePopup(popupElement);
      };
      popupElement._overlayClickHandler = handleOverlayClick;
      overlay.addEventListener('click', handleOverlayClick);
    }

    this.activePopups.push(popupElement);
  }

  closePopup(popupElement) {
    const index = this.activePopups.indexOf(popupElement);
    if (index === -1) return;

    const closedScrollEl = popupElement._scrollableElement;
    this._closePopup(popupElement);
    this.activePopups.splice(index, 1);

    if (this.activePopups.length > 0) {
      const topPopup = this.activePopups[this.activePopups.length - 1];
      if (topPopup._scrollableElement) {
        disableBodyScroll(topPopup._scrollableElement, bodyScrollLockOptions);
      }
    } else if (closedScrollEl) {
      enableBodyScroll(closedScrollEl);
    }
  }

  _closePopup(popupElement) {
    const beforeCloseEvent = new CustomEvent('popup:before-close', {
      bubbles: true,
      cancelable: true,
    });
    popupElement.dispatchEvent(beforeCloseEvent);
    if (beforeCloseEvent.defaultPrevented) return;

    const overlay = this.getOverlayElement(popupElement);
    if (overlay) {
      overlay.classList.remove('is-visible');
      overlay.classList.remove('is-popup-overlay');
      if (popupElement._overlayClickHandler) {
        overlay.removeEventListener('click', popupElement._overlayClickHandler);
        delete popupElement._overlayClickHandler;
      }
    }

    removeTrapFocus(popupElement);

    // Instant close: no transition wait. display:none kills any pending animation.
    popupElement.setAttribute('aria-expanded', 'false');
    popupElement.setAttribute('aria-hidden', 'true');
    popupElement.setAttribute('inert', '');
    popupElement.style.display = 'none';
    popupElement.dispatchEvent(new CustomEvent('popup:closed', { bubbles: true }));
    if (typeof this.onPopupClose === 'function') {
      this.onPopupClose(popupElement);
    }
  }

  handleDrawerCloseOther(event) {
    event.preventDefault();
    this.closeAllPopups();
  }

  closeAllPopups() {
    let lastScrollableElement = null;
    while (this.activePopups.length) {
      const popupElement = this.activePopups.pop();
      if (popupElement?._scrollableElement) {
        lastScrollableElement = popupElement._scrollableElement;
      }
      this._closePopup(popupElement);
    }

    if (lastScrollableElement) {
      enableBodyScroll(lastScrollableElement);
    }
  }

  getOverlayElement(popupElement) {
    const overlayClass = popupElement.getAttribute('data-overlay');
    if (!overlayClass) return null;
    return document.querySelector(`.${overlayClass}`);
  }
}

export const globalPopup = new Popup();
window.globalPopup = globalPopup;
